<script lang="ts">
  import { cn } from "$lib/utils/cn";
  import type { Snippet } from "svelte";

  interface Props {
    variant?: "default" | "primary" | "ghost" | "danger" | "entity";
    size?: "sm" | "default" | "lg" | "icon";
    entityType?: "person" | "event" | "topic" | "other";
    active?: boolean;
    disabled?: boolean;
    class?: string;
    children: Snippet;
    onclick?: (e: MouseEvent) => void;
  }

  let {
    variant = "default",
    size = "default",
    entityType,
    active = false,
    disabled = false,
    class: className,
    children,
    onclick,
    ...restProps
  }: Props = $props();

  const baseStyles = `
    inline-flex items-center justify-center
    font-medium transition-all duration-100
    focus:outline-none focus-visible:ring-1 focus-visible:ring-accent-primary
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

  const variants = {
    default: `
      bg-surface-3 border border-border-default text-text-secondary
      hover:bg-surface-4 hover:border-border-strong hover:text-text-primary
    `,
    primary: `
      bg-accent-primary border border-accent-primary text-white
      hover:bg-accent-hover hover:border-accent-hover
    `,
    ghost: `
      bg-transparent border border-transparent text-text-secondary
      hover:bg-surface-3 hover:text-text-primary
    `,
    danger: `
      bg-red-900/30 border border-red-800 text-red-400
      hover:bg-red-900/50 hover:border-red-700
    `,
    entity: `
      bg-surface-3 border text-text-secondary
      hover:bg-surface-4 hover:text-text-primary
    `,
  };

  const sizes = {
    sm: "h-[22px] px-2 text-[0.65rem] rounded-[3px]",
    default: "h-[26px] px-3 text-[0.7rem] rounded-[3px]",
    lg: "h-[32px] px-4 text-[0.75rem] rounded",
    icon: "h-[22px] w-[22px] text-[0.65rem] rounded-[3px]",
  };

  const entityColors = {
    person: "border-track-person hover:border-track-person/80",
    event: "border-track-event hover:border-track-event/80",
    topic: "border-track-topic hover:border-track-topic/80",
    other: "border-track-other hover:border-track-other/80",
  };

  const activeStyles = "bg-accent-primary/15 border-accent-primary text-accent-primary";
</script>

<button
  class={cn(
    baseStyles,
    variants[variant],
    sizes[size],
    variant === "entity" && entityType && entityColors[entityType],
    active && activeStyles,
    className
  )}
  disabled={disabled}
  onclick={onclick}
  {...restProps}
>
  {@render children()}
</button>
